import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { finalize } from 'rxjs';

import { ButtonComponent } from '../../../shared/ui/button/button.component';
import { BadgeComponent } from '../../../shared/ui/badge/badge.component';
import { PpCoinComponent } from '../../../shared/ui/pp-coin/pp-coin.component';
import { CartService } from '../../../shared/services/cart.service';
import { CartLine, PromotionalCodeDiscountType } from '../../../shared/models/cart.model';
import {
  cartAuthoritativePoints,
  cartErrorMessage,
  cartLineDiscountPct,
  cartLineGrossSubtotal,
  cartLineHasOffer,
  cartLineOriginalUnitPrice,
  cartLinePoints,
  cartLineUnitPrice,
} from '../../../shared/utils/cart.utils';
import {
  blockedLineReasonText,
  PERRON_POINTS_COPY,
  withPoints,
} from '../../../shared/perron-points/perron-points-copy';
import { ToastService } from '../../../shared/services/toast.service';
import { formatCrc } from '../../../shared/utils/currency.util';
import { environment } from '../../../../environments/environment';

const FALLBACK_IMAGE_URL = '/images/hero/broken_hero.jpg';
const EMPTY_CART_MASCOT_URL = '/images/logo/empty_cart.png';

export type CheckoutDeliveryMethod =
  | 'ps'
  | 'uber'
  | 'other_shop'
  | 'home'
  | 'ps_delivery';

@Component({
  selector: 'app-cart-summary',
  imports: [ButtonComponent, BadgeComponent, PpCoinComponent],
  templateUrl: './cart-summary.component.html',
  styleUrl: './cart-summary.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CartSummaryComponent {
  private readonly cartService = inject(CartService);
  private readonly router = inject(Router);
  private readonly toastService = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);

  // En 6a siempre será false (read-only). El input existe como API pública
  // pensando en una futura unificación con el drawer, pero las ramas
  // editables NO se implementan ahora.
  // TODO: tarea futura — renderizar controles +/- y eliminar cuando editable=true.
  readonly editable = input<boolean>(false);

  // Lo pasa el checkout-page una vez el user selecciona método de envío.
  // Null mientras no hay form bound (defensivo).
  readonly deliveryMethod = input<CheckoutDeliveryMethod | null>(null);

  // Costo de envío calculado por el padre. La fila "Envío" del resumen se
  // condiciona a que `deliveryMethod` esté bound, no a este valor — un
  // 0 explícito se renderiza como "Gratis".
  readonly deliveryPrice = input<number>(0);

  protected readonly cart = this.cartService.cart;

  protected readonly lines = computed<CartLine[]>(() => this.cart()?.lines ?? []);
  protected readonly isEmpty = computed(() => this.lines().length === 0);

  protected readonly activePromo = computed<{
    code: string;
    description: string | null;
    valueLabel: string | null;
  } | null>(() => {
    const pc = this.cart()?.promotional_code;
    if (!pc || typeof pc !== 'object' || !pc.code) return null;
    return {
      code: pc.code,
      description: typeof pc.description === 'string' && pc.description ? pc.description : null,
      valueLabel: this.formatDiscountValue(pc.discount_type, pc.discount_value),
    };
  });

  /**
   * Monto que le falta al usuario para desbloquear envío gratis. Sólo aplica
   * a `'home'` (Correos de CR): el envío tiene precio fijo que se elimina al
   * superar el umbral. `'ps_delivery'` se cotiza al recibir el pedido, así que
   * mostrar un threshold ahí sería contradictorio. Otros métodos no tienen
   * política de envío gratis.
   */
  protected readonly freeShippingMissing = computed<number | null>(() => {
    const cart = this.cart();
    const method = this.deliveryMethod();
    if (!cart || !cart.ps_delivery) return null;
    if (method !== 'home') return null;
    if (!cart.ps_delivery.free_delivery) return null;
    const missing = cart.ps_delivery.min_for_free - cart.subtotal;
    return missing > 0 ? missing : null;
  });

  protected readonly mascotUrl = EMPTY_CART_MASCOT_URL;

  protected readonly ppCopy = PERRON_POINTS_COPY.checkout;
  protected readonly withPoints = withPoints;

  /**
   * Líneas de Acceso VIP bloqueadas (R4/R6), derivadas en `CartService`. El
   * resumen es read-only salvo aquí: «Quitar del carrito» es la única salida
   * del cliente con saldo corto, así que la acción tiene que estar donde ve el
   * bloqueo, sin obligarle a volver al drawer.
   */
  protected readonly blockedLines = this.cartService.blockedLines;
  protected readonly blockedNotice = this.cartService.blockedLinesNotice;
  protected readonly blockedCopy = PERRON_POINTS_COPY.blockedLine;
  protected readonly removingLineId = signal<number | null>(null);

  protected isLineBlocked(lineId: number): boolean {
    return this.blockedLines().has(lineId);
  }

  /** Motivo del bloqueo, con la variante de umbral 0. */
  protected blockedReason(lineId: number): string {
    return blockedLineReasonText(this.blockedLines().get(lineId) ?? 0);
  }

  protected onRemoveBlocked(line: CartLine): void {
    if (this.removingLineId() !== null) return;
    this.removingLineId.set(line.id);

    this.cartService
      .removeLine(line.id)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.removingLineId.set(null)),
      )
      .subscribe({
        error: (err) => {
          const message = cartErrorMessage(err, 'No pudimos eliminar el producto.');
          if (message) this.toastService.show({ type: 'error', message });
        },
      });
  }

  /** Fila autoritativa de Perrón Points de esta compra (mirror de D13, sin envío). */
  protected readonly authoritativePoints = computed(() => cartAuthoritativePoints(this.cart()));

  protected linePoints(line: CartLine): number {
    return cartLinePoints(line);
  }

  protected onExploreClick(): void {
    this.router.navigate(['/tienda']);
  }

  protected lineImageUrl(line: CartLine): string {
    const first = line.product?.images?.[0];
    if (!first) return FALLBACK_IMAGE_URL;
    return `${environment.imagesUrl}products/${first.name}`;
  }

  protected onImageError(event: Event): void {
    const img = event.target as HTMLImageElement | null;
    if (img && !img.src.endsWith(FALLBACK_IMAGE_URL)) {
      img.src = FALLBACK_IMAGE_URL;
    }
  }

  protected formatCRC(amount: number): string {
    return formatCrc(amount);
  }

  // Precios de línea con IVA (paridad con el catálogo). Delegan en cart.utils;
  // `line.sale_price`/`line.subtotal` son netos y no deben pintarse directo.
  protected lineUnitPrice(line: CartLine): number {
    return cartLineUnitPrice(line);
  }

  protected lineSubtotal(line: CartLine): number {
    return cartLineGrossSubtotal(line);
  }

  protected lineHasOffer(line: CartLine): boolean {
    return cartLineHasOffer(line);
  }

  protected lineOriginalPrice(line: CartLine): number | null {
    return cartLineOriginalUnitPrice(line);
  }

  protected lineDiscountPct(line: CartLine): number {
    return Math.round(cartLineDiscountPct(line));
  }

  private formatDiscountValue(
    type: PromotionalCodeDiscountType | undefined,
    value: number | undefined,
  ): string | null {
    if (typeof value !== 'number' || !Number.isFinite(value)) return null;
    if (type === 'percentage') return `${value}%`;
    if (type === 'fixed') return this.formatCRC(value);
    return null;
  }
}
